import { useState } from "react";
import { formatCurrency } from "../../../utils/formatters";
import { useUpdatePaymentReferenceMutation } from "../api/billingApi";

interface Props {
  payment: any;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

/**
 * Correct the gateway reference on a payment that has not settled.
 *
 * Verification asks OnePay about this one field and nothing else, so a
 * reference captured wrongly at the counter leaves real money stuck: Reverify
 * asks about a transaction the gateway has never heard of, gets "no record"
 * back, and that is indistinguishable from "never paid". Fixing the reference
 * is what makes the payment claimable again — which is why the modal closes
 * by telling the admin to reverify, not by implying the money has arrived.
 *
 * Settled payments never reach here: the register only offers the button while
 * the status is unpaid, and the API refuses 'paid' regardless.
 */
export default function EditPaymentReferenceModal({ payment, onClose, onSuccess }: Props) {
  const [reference, setReference] = useState(payment.reference || "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  // Set when the API refuses because another payment already holds the
  // reference. Shown separately from `error` because it is actionable: that
  // other row is very often the attempt the money really went through on.
  const [conflict, setConflict] = useState<any>(null);

  const [updateReference, { isLoading }] = useUpdatePaymentReferenceMutation();

  const trimmed = reference.trim();
  const unchanged = trimmed === (payment.reference || "").trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmed) {
      setError("A reference is required — verification has nothing to ask the gateway about without one.");
      return;
    }
    if (unchanged) {
      setError("That is already this payment's reference.");
      return;
    }
    setError("");
    setConflict(null);
    try {
      const res: any = await updateReference({
        id: payment.id,
        body: { reference: trimmed, reason: reason.trim() || undefined },
      }).unwrap();
      onSuccess(res?.message || "Reference updated. Reverify the payment to ask the gateway about it.");
    } catch (err: any) {
      setConflict(err?.data?.conflicting_payment ?? null);
      setError(err?.data?.message || "Could not update the reference. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-black text-slate-800 uppercase tracking-widest">
              Edit Payment Reference
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
              Unsettled payments only · takes effect immediately
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Payment summary */}
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
              Payment being corrected
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Payer</span>
                <span className="font-black text-slate-800">
                  {payment.user?.name || payment.payer_name || "—"}
                </span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Amount</span>
                <span className="font-black text-slate-800">{formatCurrency(payment.amount)}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Status</span>
                <span className="font-black text-amber-600 capitalize">{payment.status || "—"}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] block">Current Ref</span>
                <span className="font-mono text-slate-700 text-[10px] break-all">{payment.reference || "—"}</span>
              </div>
            </div>
          </div>

          {/* What this actually does */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2">
            <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-[10px] font-bold text-blue-700">
              This changes which gateway transaction the payment refers to. It does not collect or
              settle anything — run <span className="uppercase">Reverify</span> afterwards to ask
              OnePay about the new reference.
            </p>
          </div>

          {/* New reference */}
          <div>
            <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest block mb-1">
              Gateway Reference <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. NIQS-1A2B3C4D"
              autoFocus
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100"
            />
            <p className="text-[9px] font-bold text-slate-400 mt-1">
              Must match the transaction reference OnePay holds, exactly.
            </p>
          </div>

          {/* Reason — optional, but it is what the audit trail shows later */}
          <div>
            <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest block mb-1">
              Reason <span className="text-slate-300 normal-case tracking-normal font-bold">(optional)</span>
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why the reference is being changed — this is what the audit trail shows."
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100 resize-none"
            />
          </div>

          {error && (
            <div className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2 space-y-1">
              <p>{error}</p>
              {conflict && (
                <p className="font-mono text-[10px] text-rose-500 font-normal">
                  Held by payment {String(conflict.id).slice(0, 8)}… · {conflict.status}
                  {conflict.amount != null ? ` · ${formatCurrency(conflict.amount)}` : ""}
                  {conflict.receipt_number ? ` · receipt ${conflict.receipt_number}` : ""}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !trimmed || unchanged}
              className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-slate-900 text-white hover:bg-orange-600 transition-all disabled:opacity-50"
            >
              {isLoading ? "Saving..." : "Save Reference"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
